/* eslint-disable arrow-body-style */
import { environment } from 'apps/dawba-questionnaire-app/src/environments/environment';
import * as jsonpath from 'jsonpath';

import { Injectable } from '@angular/core';

import { NavController } from '@ionic/angular';

import { EMPTY, defer, forkJoin, from, iif, of } from 'rxjs';
import {
  catchError,
  concatMap,
  filter,
  map,
  mergeMap,
  switchMap,
  switchMapTo,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import { HestiaFhirResourcesService, anyCarePlanActivitiesBegun } from '@hestia/ngx-fhir';
import { completeSessionSuccess, qnrFormSessionActions } from '@hestia/ngx-fhir-qnr-forms';
import { HestiaFhirResourceTypeName } from '@hestia/ngx-types';
import { userActions } from '@hestia/ngx-user';

import { DawbaRoutes } from '../../route-constants';
import { DawbaAppService } from '../../services/app.service';
import { dawbaSpecificActions } from './dawba-specific.actions';
import { selectDawbaCarePlan } from './dawba-specific.selectors';

@Injectable()
export class DawbaSpecificEffects {
  constructor(
    private actions$: Actions,
    private fhirService: HestiaFhirResourcesService,
    private store: Store,
    private dawba: DawbaAppService,
    private navCtrl: NavController
  ) {}

  // eslint-disable-next-line arrow-body-style
  loadUserProfileSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(userActions.userProfileLoadSuccess),
      map((_action) => dawbaSpecificActions.initPrimaryFhirLoad())
    );
  });

  logoutUserSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(userActions.logoutSuccess),
        map((_action) => this.dawba.handleLogout({ navigateToRoot: true }))
      );
    },
    { dispatch: false }
  );

  logoutUserFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(userActions.logoutFailure),
        map((_action) => this.dawba.handleLogout({ navigateToRoot: true }))
      );
    },
    { dispatch: false }
  );

  // eslint-disable-next-line arrow-body-style
  initPrimaryFhirLoad$ = createEffect(() => {
    console.log('initPrimaryFhirLoad$');
    return this.actions$.pipe(
      ofType(dawbaSpecificActions.initPrimaryFhirLoad),
      mergeMap((_action) =>
        this.fhirService.fetchUncanceledUserCarePlan().pipe(
          mergeMap((carePlanBundle) => {
            const referencedQnrUrls = Array.from(
              new Set(
                carePlanBundle.entry
                  .map((bundleEntry) => {
                    const carePlan = bundleEntry.resource as fhir4.CarePlan;
                    // eslint-disable-next-line ngrx/no-multiple-actions-in-effects
                    return carePlan.activity.map((activity) => {
                      const instantiatesCanonical = activity.detail?.instantiatesCanonical;
                      return instantiatesCanonical?.length > 0 ? instantiatesCanonical[0] : null;
                    });
                  })
                  .filter((x) => x !== null)
                  .reduce((prev, cur) => {
                    // eslint-disable-next-line ngrx/no-multiple-actions-in-effects
                    return [...prev, ...cur];
                  }, [])
              )
            );
            const referencedQnrResponseIds =
              Array.from(
                new Set(
                  carePlanBundle.entry
                    .map((bundleEntry) => {
                      const carePlan = bundleEntry.resource as fhir4.CarePlan;
                      // eslint-disable-next-line ngrx/no-multiple-actions-in-effects
                      return carePlan.activity.map((activity) => {
                        const qnrResponseRefererence: fhir4.Reference = activity.outcomeReference?.[0] ?? null;
                        const qnrResponseId: string = qnrResponseRefererence?.reference?.split('/')[1] ?? null;
                        return qnrResponseId;
                      });
                    })
                    .reduce((prev, cur) => {
                      return [...prev, ...cur];
                    }, [])
                )
              ) ?? [];
            if (referencedQnrResponseIds.length == 0 && referencedQnrUrls.length == 0) {
              this.navCtrl.navigateRoot(DawbaRoutes.NoTaskAvailablePage);
            }
            return forkJoin([
              ...referencedQnrResponseIds
                .filter((x) => x !== null)
                .map((id) => this.fhirService.fetchResource(HestiaFhirResourceTypeName.QuestionnaireResponse, id)),
              ...referencedQnrUrls.map((url) => {
                const qnrRequest$ = this.fhirService.fetchResource(
                  HestiaFhirResourceTypeName.Questionnaire,
                  url.split('/').pop(),
                  { queryStoreFirst: true, addToStore: true }
                );
                // TODO: For now we know that URLs aways end with the UUID of the resource, so we can do following trick
                return iif(
                  () => environment.prefetchValueSets,
                  qnrRequest$.pipe(
                    concatMap((questionnaire: fhir4.Questionnaire) => {
                      if (environment.prefetchValueSets) {
                        const valueSets = Array.from(
                          new Set(jsonpath.query(questionnaire, '$..answerValueSet'))
                        ) as string[];
                        // TODO: For now we know that URLs aways end with the UUID of the resource, so we can do following trick
                        return forkJoin(
                          valueSets.map((valueSet) =>
                            this.fhirService.fetchResource(
                              HestiaFhirResourceTypeName.ValueSet,
                              valueSet.split('/').pop(),
                              { queryStoreFirst: true, addToStore: true }
                            )
                          )
                        );
                      }
                    })
                  ),
                  qnrRequest$
                );
              }),
            ]);
          }),
          map((_result) => dawbaSpecificActions.primaryFhirLoadSuccess()),
          catchError((error) => {
            console.log({ error });
            return of(dawbaSpecificActions.primaryFhirLoadFailure());
          })
        )
      )
    );
  });

  primaryFhirLoadSuccess$ = createEffect(
    // eslint-disable-next-line arrow-body-style
    () => {
      return this.actions$.pipe(
        ofType(dawbaSpecificActions.primaryFhirLoadSuccess),
        switchMap((_action) =>
          this.fhirService.carePlans$.pipe(
            take(1),
            switchMap((carePlans) => {
              const sorted = carePlans.sort((a, b) => (a.created <= b.created ? 1 : -1));
              const newestCarePlan = sorted[sorted.length - 1] ?? null;
              const selectedCarePlan =
                sorted.find((carePlan) => carePlan.status === 'active') ??
                sorted.find((carePlan) => carePlan.status === 'draft') ??
                sorted.find((carePlan) => carePlan.status === 'unknown');
              if (selectedCarePlan) {
                return of(
                  dawbaSpecificActions.setCurCarePlan({
                    curCarePlanId: selectedCarePlan.id,
                  })
                );
              }
              const completedCarePlans = sorted.filter((carePlan) => carePlan.status === 'completed');
              if (completedCarePlans.length === 0) {
                return of(
                  dawbaSpecificActions.setCurCarePlan({
                    curCarePlanId: newestCarePlan?.id,
                  })
                );
              }
              return forkJoin(
                completedCarePlans.map((carePlan) =>
                  this.store.select(selectDawbaCarePlan(carePlan.id)).pipe(
                    filter((dawbaCarePlan) => dawbaCarePlan !== null && dawbaCarePlan !== undefined),
                    take(1)
                  )
                )
              ).pipe(
                map((dawbaCarePlans) => {
                  const selectedCompletedCarePlan =
                    dawbaCarePlans.find((dawbaCarePlan) => !this.isDawbaFinishDone(dawbaCarePlan))?.carePlanResource ??
                    newestCarePlan;
                  return dawbaSpecificActions.setCurCarePlan({
                    curCarePlanId: selectedCompletedCarePlan?.id,
                  });
                })
              );
            })
          )
        )
      );
    }
  );

  submitQnrSessionPage$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(qnrFormSessionActions.submitSessionPage),
        switchMapTo(defer(() => from(this.dawba.common.presentLoader())))
      );
    },
    { dispatch: false }
  );

  submitQnrSessionPageSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(qnrFormSessionActions.submitSessionPageSuccess),
        switchMapTo(defer(() => from(this.dawba.common.dismissLoader())))
      );
    },
    { dispatch: false }
  );

  // eslint-disable-next-line arrow-body-style
  setCurCarePlan$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(dawbaSpecificActions.setCurCarePlan),
      switchMap((action) =>
        this.store.select(selectDawbaCarePlan(action.curCarePlanId)).pipe(
          filter((carePlan) => carePlan !== null && carePlan !== undefined),
          take(1)
        )
      ),
      concatMap((dawbaCarePlan) => {
        if (!dawbaCarePlan) {
          this.navCtrl.navigateRoot(DawbaRoutes.NoTaskAvailablePage);
        }
        const anyActivitiesBegun = anyCarePlanActivitiesBegun(dawbaCarePlan.carePlanResource);
        console.log({ dawbaCarePlan });
        switch (dawbaCarePlan.carePlanResource?.status) {
          case 'draft':
          case 'active':
          case 'unknown':
            if (!anyActivitiesBegun) {
              return of(
                dawbaSpecificActions.setCurCarePlanSuccess({
                  route: environment.enableOnboarding ? DawbaRoutes.OnboardingPage : DawbaRoutes.IntroToIntroPage,
                })
              );
            } else {
              return of(
                dawbaSpecificActions.setCurCarePlanSuccess({
                  route:
                    dawbaCarePlan.introActivity?.status === 'completed'
                      ? DawbaRoutes.DashboardPage
                      : DawbaRoutes.IntroToIntroPage,
                })
              );
            }
          case 'completed':
            if (!this.isDawbaFinishDone(dawbaCarePlan)) {
              return of(
                dawbaSpecificActions.setCurCarePlanSuccess({
                  route:
                    dawbaCarePlan.introActivity?.status === 'completed'
                      ? DawbaRoutes.DashboardPage
                      : DawbaRoutes.IntroToIntroPage,
                })
              );
            }

          default:
            return of(
              dawbaSpecificActions.setCurCarePlanFailure({
                route: DawbaRoutes.NoTaskAvailablePage,
              })
            );
        }
      })
    );
  });

  // eslint-disable-next-line arrow-body-style
  setCurCarePlanSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(dawbaSpecificActions.setCurCarePlanSuccess),
        tap((action) => this.navCtrl.navigateRoot(action.route))
      );
    },
    { dispatch: false }
  );

  // eslint-disable-next-line arrow-body-style
  setCurCarePlanFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(dawbaSpecificActions.setCurCarePlanFailure),
        tap((action) => this.navCtrl.navigateRoot(action.route))
      );
    },
    { dispatch: false }
  );

  completeQnrSession$ = createEffect(
    () =>
      // eslint-disable-next-line arrow-body-style
      {
        return this.actions$.pipe(
          ofType(completeSessionSuccess),
          tap(() => this.navCtrl.navigateRoot(DawbaRoutes.DashboardPage))
        );
      },
    { dispatch: false }
  );

  private isDawbaFinishDone(dawbaCarePlan): boolean {
    const values = this.dawba.getDawbaActivityValues(dawbaCarePlan?.finishActivity);
    return (
      !!values &&
      (values['tfinish'] === '1' ||
        values['finish'] === '1' ||
        values['pfinish'] === '1' ||
        values['sfinish'] === '1')
    );
  }
}
